import React, { useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Lock } from "lucide-react";
import { api, fmtError } from "@/lib/api";
import { fmtEUR } from "@/constants/labels";

/** Édition d'un BROUILLON de facture (jamais émis, sans numéro). */
export const FactureBrouillonModal = ({ facture, commande, onClose, onSaved }) => {
  const [lignes, setLignes] = useState(facture.lignes.map((l) => ({ ...l })));
  const [dateEch, setDateEch] = useState(facture.date_echeance || "");
  const [dateExec, setDateExec] = useState(facture.date_execution || "");
  const [notes, setNotes] = useState(facture.notes || "");
  const [busy, setBusy] = useState(false);
  const upd = (i, k, v) => setLignes(lignes.map((l, j) => (j === i ? { ...l, [k]: v } : l)));
  const total = lignes.reduce((s, l) => s + Number(l.quantite || 0) * Number(l.prix_unitaire_ht || 0), 0);
  const reste = commande ? commande.reste_a_facturer_ht : null;
  const over = reste != null && total > reste + 0.01;

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/factures/${facture.id}`, {
        lignes: lignes.map((l) => ({ ...l, quantite: Number(l.quantite), prix_unitaire_ht: Number(l.prix_unitaire_ht), taux_tva: Number(l.taux_tva || 0) })),
        date_echeance: dateEch || null, date_execution: dateExec || null, notes,
      });
      toast.success("Brouillon enregistré"); onSaved();
    } catch (err) { toast.error(fmtError(err)); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="card p-4 w-full max-w-3xl my-6" onClick={(e) => e.stopPropagation()} data-testid="facture-brouillon-modal">
        <h3 className="font-semibold text-[#1F2933]">Brouillon de facture {facture.commande_numero ? `— commande ${facture.commande_numero}` : ""}</h3>
        <p className="text-xs text-[#3E4C59] mt-1 inline-flex items-center gap-1"><Lock size={12} /> Parties, SIRET et référence de commande sont figés. Le numéro sera attribué à l'émission.</p>
        <div className="mt-3 overflow-x-auto">
          <table className="table-compact w-full">
            <thead><tr><th>Désignation</th><th>Qté</th><th>Unité</th><th>PU HT</th><th></th></tr></thead>
            <tbody>
              {lignes.map((l, i) => (
                <tr key={i}>
                  <td><input className="input" value={l.designation} onChange={(e) => upd(i, "designation", e.target.value)} data-testid={`brouillon-designation-${i}`} /></td>
                  <td><input type="number" step="0.01" className="input w-24" value={l.quantite} onChange={(e) => upd(i, "quantite", e.target.value)} data-testid={`brouillon-qte-${i}`} /></td>
                  <td><input className="input w-24" value={l.unite} onChange={(e) => upd(i, "unite", e.target.value)} /></td>
                  <td><input type="number" step="0.01" className="input w-28" value={l.prix_unitaire_ht} onChange={(e) => upd(i, "prix_unitaire_ht", e.target.value)} data-testid={`brouillon-pu-${i}`} /></td>
                  <td>{lignes.length > 1 && <button type="button" className="btn-ghost p-1" onClick={() => setLignes(lignes.filter((_, j) => j !== i))} data-testid={`brouillon-del-${i}`}><Trash2 size={14} /></button>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="mt-2 flex gap-2">
          <button type="button" className="btn-outline text-xs" onClick={() => setLignes([...lignes, { designation: "", quantite: 1, unite: "forfait", prix_unitaire_ht: 0, taux_tva: lignes[0]?.taux_tva || 0 }])}><Plus size={12} /> Ligne</button>
          <button type="button" className="btn-outline text-xs" onClick={() => setLignes([...lignes, { designation: "Retenue de garantie 5 %", quantite: 1, unite: "forfait", prix_unitaire_ht: -Math.round(total * 5) / 100, taux_tva: lignes[0]?.taux_tva || 0 }])} data-testid="brouillon-add-retenue"><Plus size={12} /> Retenue / escompte</button>
        </div>
        <div className="grid sm:grid-cols-3 gap-3 mt-4">
          <div><label className="label">Date d'exécution</label><input type="date" className="input" value={dateExec} onChange={(e) => setDateExec(e.target.value)} /></div>
          <div><label className="label">Échéance de paiement</label><input type="date" className="input" value={dateEch} onChange={(e) => setDateEch(e.target.value)} data-testid="brouillon-echeance" /></div>
          <div className="sm:col-span-3"><label className="label">Note libre</label><textarea className="textarea" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="brouillon-notes" /></div>
        </div>
        <div className={`mt-4 text-sm ${over ? "text-[#C0392B]" : "text-[#1F2933]"}`} data-testid="brouillon-total">
          Total HT : <b>{fmtEUR(total)}</b>{reste != null && <> — reste à facturer sur la commande : <b>{fmtEUR(reste)}</b>{over && " — dépassement : avenant requis"}</>}
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <button className="btn-ghost" onClick={onClose}>Fermer</button>
          <button className="btn-primary" disabled={busy || over || total <= 0} onClick={save} data-testid="brouillon-save">Enregistrer le brouillon</button>
        </div>
      </div>
    </div>
  );
};

export const EmettreRecapModal = ({ facture, commande, onClose, onConfirm }) => (
  <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
    <div className="card p-4 w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="emettre-recap-modal">
      <h3 className="font-semibold text-[#1F2933]">Émettre la facture</h3>
      <dl className="mt-3 text-sm space-y-1">
        <div className="flex justify-between"><dt>Montant HT</dt><dd><b>{fmtEUR(facture.montant_ht)}</b></dd></div>
        <div className="flex justify-between"><dt>TVA</dt><dd>{fmtEUR(facture.montant_tva || 0)} <span className="text-[#94A3B8]">({facture.regime_tva})</span></dd></div>
        <div className="flex justify-between"><dt>Total TTC</dt><dd><b>{fmtEUR(facture.montant_ttc)}</b></dd></div>
        {commande && <div className="flex justify-between border-t pt-1 mt-1"><dt>Reste sur commande {commande.numero} après émission</dt><dd data-testid="emettre-recap-reste"><b>{fmtEUR(commande.reste_a_facturer_ht - facture.montant_ht)}</b></dd></div>}
      </dl>
      <p className="text-xs text-[#3E4C59] mt-3">Le numéro sera attribué maintenant, la facture sera validée EN 16931 puis scellée : plus aucune modification ne sera possible.</p>
      <div className="flex justify-end gap-2 mt-4">
        <button className="btn-ghost" onClick={onClose}>Annuler</button>
        <button className="btn-accent" onClick={onConfirm} data-testid="emettre-recap-confirm">Émettre la facture</button>
      </div>
    </div>
  </div>
);
